import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Star, Share2, FileText, Users, Send } from "lucide-react";
import { toast } from "sonner";

const typeIcons = { social_share: Share2, content: FileText, referral: Users };
const typeColors = { social_share: "text-blue-400", content: "text-green-400", referral: "text-[#F7941D]" };

export default function TasksPage() {
  const [tasks, setTasks] = useState([]);
  const [proofUrl, setProofUrl] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [openDialog, setOpenDialog] = useState(null);

  useEffect(() => { fetchTasks(); }, []);

  const fetchTasks = async () => {
    try { const { data } = await api.get("/tasks"); setTasks(data); }
    catch (err) { console.error(err); }
  };

  const handleSubmit = async (taskId) => {
    if (!proofUrl.trim()) { toast.error("Please provide a proof URL"); return; }
    setSubmitting(true);
    try {
      await api.post(`/tasks/${taskId}/submit`, { proof_url: proofUrl, notes });
      toast.success("Task submitted for review!");
      setOpenDialog(null); setProofUrl(""); setNotes("");
      fetchTasks();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="space-y-6 max-w-5xl" data-testid="tasks-page">
      <div className="animate-fade-in-up">
        <h1 className="text-2xl sm:text-3xl font-semibold text-white/90 tracking-tight">Tasks & Challenges</h1>
        <p className="text-white/35 text-sm mt-1">Complete tasks to earn points and rewards</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 stagger">
        {tasks.map((t) => {
          const Icon = typeIcons[t.type] || Star;
          const sub = t.user_submission;
          return (
            <div key={t.id} className="glass glass-hover rounded-2xl p-5 animate-fade-in-up" data-testid={`task-card-${t.id}`}>
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Icon className={`w-4 h-4 ${typeColors[t.type] || "text-white/40"}`} strokeWidth={1.5} />
                  <Badge variant="outline" className="text-[10px] border-white/[0.08] uppercase tracking-wider bg-white/[0.04] text-white/40">{t.type?.replace("_", " ")}</Badge>
                </div>
                <div className="flex items-center gap-1.5 bg-[#F7941D]/10 px-2.5 py-1 rounded-full">
                  <Star className="w-3.5 h-3.5 text-[#F7941D]" fill="currentColor" />
                  <span className="font-mono text-sm text-[#F7941D] font-medium">{t.points}</span>
                </div>
              </div>
              <h3 className="font-medium text-white/80 mb-1">{t.title}</h3>
              <p className="text-xs text-white/30 mb-4 leading-relaxed">{t.description}</p>

              {sub ? (
                <Badge className={
                  sub.status === "approved" ? "bg-green-500/10 text-green-400 border-green-500/20" :
                  sub.status === "rejected" ? "bg-red-500/10 text-red-400 border-red-500/20" :
                  "bg-yellow-500/10 text-yellow-400 border-yellow-500/20"
                } data-testid={`task-status-${t.id}`}>
                  {sub.status}
                </Badge>
              ) : (
                <Dialog open={openDialog === t.id} onOpenChange={(open) => { setOpenDialog(open ? t.id : null); if (!open) { setProofUrl(""); setNotes(""); } }}>
                  <DialogTrigger asChild>
                    <button className="bg-[#F7941D] hover:bg-[#ff9f2e] text-black font-semibold rounded-full px-4 py-2 text-xs transition-all duration-300" data-testid={`submit-task-${t.id}`}>
                      <Send className="w-3 h-3 inline mr-2" />
                      Submit Proof
                    </button>
                  </DialogTrigger>
                  <DialogContent className="glass-strong rounded-2xl border-white/[0.08]">
                    <DialogHeader>
                      <DialogTitle className="text-white/90">Submit: {t.title}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 mt-4">
                      <div className="space-y-2">
                        <Label className="text-white/60 text-sm font-medium">Proof URL</Label>
                        <Input value={proofUrl} onChange={e => setProofUrl(e.target.value)} placeholder="https://twitter.com/your-post" className="glass-input rounded-xl h-11 text-sm" data-testid="proof-url-input" />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-white/60 text-sm font-medium">Notes <span className="text-white/25">(optional)</span></Label>
                        <Input value={notes} onChange={e => setNotes(e.target.value)} placeholder="Any additional context" className="glass-input rounded-xl h-11 text-sm" data-testid="proof-notes-input" />
                      </div>
                      <button className="w-full bg-[#F7941D] hover:bg-[#ff9f2e] text-black font-semibold rounded-full h-11 text-sm transition-all duration-300" onClick={() => handleSubmit(t.id)} disabled={submitting} data-testid="submit-proof-button">
                        {submitting ? "Submitting..." : "Submit for Review"}
                      </button>
                    </div>
                  </DialogContent>
                </Dialog>
              )}
            </div>
          );
        })}
        {tasks.length === 0 && (
          <div className="col-span-full text-center text-white/25 py-12">No active tasks</div>
        )}
      </div>
    </div>
  );
}
